/**
 * Stat icons — the number-glyph family: glyphs that label the game's recurring numeric concepts
 * on stats surfaces (leaderboard, /games, round-inspection, /help). Designed together so the set
 * reads as consistent and connected — one merchant's-table through-line (worth-icon task; the
 * full candidate set and Andre's verdicts live in dev/number-glyphs-spike.js, which keeps the
 * lab-only runners-up). Bilanz/net gets one object in two cuts: createBalanceScale at large homes
 * (≥ ~28px — the AVG NET KPI strip, /help), createBilanzScaleChip below that (the /games row net
 * icon, leaderboard-detail avg rows) — the standard family's full-size/chip-grade precedent. All
 * draw with currentColor so they inherit the row's text color.
 */
import { SVG_NS, svg, path, filledDot } from "./svg.js";

/** A stroke-only circle — the bid weight's ring handle. Module-local (CODING: locality over
 *  shared utilities) — dev/number-glyphs-spike.js keeps its own copy for its still-lab-only eye
 *  candidate; promote to svg.js only if a second icons/ module needs it. */
function strokeCircle(cx, cy, r, strokeWidth = 2.5) {
  const el = document.createElementNS(SVG_NS, "circle");
  el.setAttribute("cx", cx);
  el.setAttribute("cy", cy);
  el.setAttribute("r", r);
  el.setAttribute("stroke", "currentColor");
  el.setAttribute("stroke-width", strokeWidth);
  el.setAttribute("fill", "none");
  return el;
}

/** Average / Bilanz — merchant's balance scale, level beam. The large-home cut; see
 *  createBilanzScaleChip below for the chip-grade sibling. */
export function createBalanceScale(size) {
  const el = svg(size, size);
  // Post, finial, base
  el.appendChild(path("M24 12 L24 39", 2.5));
  el.appendChild(filledDot(24, 9.5, 2));
  el.appendChild(path("M16 42 L32 42", 2.5));
  // Beam
  el.appendChild(path("M11 14 L37 14", 2.5));
  // Strings, V down to each pan
  el.appendChild(path("M11 14 L7 23 M11 14 L15 23", 1.3));
  el.appendChild(path("M37 14 L33 23 M37 14 L41 23", 1.3));
  // Pans
  el.appendChild(path("M5.5 23.5 Q11 30 16.5 23.5", 2.2));
  el.appendChild(path("M31.5 23.5 Q37 30 42.5 23.5", 2.2));
  return el;
}

/** Bilanz/net — the balance scale re-cut chip-grade: shorter post, heavier strings, wider pans,
 *  so it survives 14–22px next to a signed number. One object, two cuts — createBalanceScale
 *  above is the full-size sibling for homes ≥ ~28px. */
export function createBilanzScaleChip(size) {
  const el = svg(size, size);
  el.appendChild(path("M24 13 L24 38", 2.5)); // post
  el.appendChild(filledDot(24, 10.5, 2.2)); // finial
  el.appendChild(path("M15 41 L33 41", 2.5)); // base
  el.appendChild(path("M9 15 L39 15", 2.5)); // beam
  el.appendChild(path("M9 15 L5 24 M9 15 L13 24", 2)); // strings, heavier
  el.appendChild(path("M39 15 L35 24 M39 15 L43 24", 2));
  el.appendChild(path("M3.5 24.5 Q9 32 14.5 24.5", 2.5)); // pans, wider
  el.appendChild(path("M33.5 24.5 Q39 32 44.5 24.5", 2.5));
  return el;
}

/** Bid — the merchant's weight: what you committed to the scale in the auction; an overbid is a
 *  weight the goods can't match. Pairs visually with the Bilanz balance scale. */
export function createBidWeight(size) {
  const el = svg(size, size);
  el.appendChild(strokeCircle(24, 12, 4.2, 2.2)); // ring handle
  el.appendChild(path("M17 18 L31 18 L35 38 L13 38 Z", 2.5)); // frustum body
  el.appendChild(path("M16.5 25.5 L31.5 25.5", 1.4)); // stamped band
  return el;
}

/** Worth — the strongbox: the prize the round was played over, a craft object like the game-type
 *  chalice. Plain v1 (no gleam) — direct money depictions (coins) stay out of the family. */
export function createWorthChest(size) {
  const el = svg(size, size);
  el.appendChild(path("M8 39 L8 24 L40 24 L40 39 Z", 2.5)); // box
  el.appendChild(path("M8 24 Q8 11.5 20 11.5 L28 11.5 Q40 11.5 40 24", 2.5)); // domed lid
  el.appendChild(filledDot(24, 29, 2.3)); // keyhole
  el.appendChild(path("M24 31.4 L24 34.6", 2));
  return el;
}

/** Augen — the Zählbogen: the cards' own tally arcs (one per point, bottom-left corner of every
 *  counting card), cut loose as a standalone mark — the icon the deck already taught. */
export function createAugenTallyArcs(size) {
  const el = svg(size, size);
  // The card corner the arcs radiate from — standalone, the arcs alone read as a scratch; the
  // corner restores the context the card gives them.
  el.appendChild(path("M4 22 L4 42 L24 42", 2.5));
  // Elliptical arcs at the cards' golden tally aspect (rise = φ × run, φ ≈ 1.618 — adopted
  // 2026-07-19, DESIGN #15; was 1.4, the 5:7 echo), anchored bottom-left like
  // cards.js#addPointValueTally. The rx ladder was refit against the OLD aspect's ry ceiling
  // (15.4 / 26.6 / 37.8, this icon's own 48-unit viewBox headroom) rather than carried over
  // unchanged — a blind constant swap would have pushed the largest arc's ry to 43.7, past the
  // viewBox; rx ≈ 9.5 / 16.4 / 23.4 lands ry ≈ 15.4 / 26.5 / 37.9, holding the same footprint and
  // top clearance (42 - 37.86 ≈ 4.14, next to today's 4.2 floor).
  // Each arc starts BORDER_GAP units clear of the x=4 vertical edge — the start point is walked
  // down the ellipse from its apex to x = 4 + BORDER_GAP (y solved from the ellipse equation) —
  // so the straight corner path alone reads as the left edge and the arcs emerge beside it
  // rather than doubling its stroke into a wiggly line.
  const BORDER_GAP = 3;
  for (const rx of [9.5, 16.4, 23.4]) {
    const ry = rx * 1.618;
    const startY =
      Math.round((42 - ry * Math.sqrt(1 - (BORDER_GAP / rx) ** 2)) * 1000) /
      1000;
    el.appendChild(
      path(
        `M ${4 + BORDER_GAP},${startY} A ${rx},${ry} 0 0 0 ${4 + rx},42`,
        2.5,
      ),
    );
  }
  return el;
}
